import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Paper,
    Stack,
    InputAdornment,
    IconButton,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Chip,
} from '@mui/material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import StyleIcon from '@mui/icons-material/Style';
import KeyIcon from '@mui/icons-material/Key';
import LabelIcon from '@mui/icons-material/Label';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { CategoryFormData } from '../../../types';



const AddCategory: React.FC = () => {
    const navigate = useNavigate();
    const [optionsInputs, setOptionsInputs] = useState<{ [key: number]: string }>({});
    const [optionsChips, setOptionsChips] = useState<{ [key: number]: string[] }>({});
    const [aliasInput, setAliasInput] = useState<string>('');
    const [aliasChips, setAliasChips] = useState<string[]>([]);
    const [loading, setLoading] = useState(false)

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm<CategoryFormData>({
        defaultValues: {
            name: '',
            aliasNames: [],
            description: '',
            attributesTemplate: [],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'attributesTemplate',
    });

    const attributeTypes = [
        { value: 'string', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'select', label: 'Select' },
        { value: 'boolean', label: 'Boolean' },
        { value: 'date', label: 'Date' },
    ];

    const onSubmit = async (data: CategoryFormData) => {
        try {
            setLoading(true);

            // Process the data before sending
            const processedData = {
                ...data,
                aliasNames: aliasChips.filter(alias => alias.trim() !== ''),
                attributesTemplate: data.attributesTemplate.map((attr, index) => ({
                    ...attr,
                    // Ensure options is always an array and handle empty cases
                    options: attr.type === 'select' ? (optionsChips[index] || []).filter(opt => opt.trim() !== '') : []
                }))
            };

            // Validate that there are no duplicate keys in attributes
            const keys = processedData.attributesTemplate.map(attr => attr.key);
            const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
            if (duplicateKeys.length > 0) {
                toast.error('Duplicate attribute keys found. Please use unique keys for each attribute.');
                return;
            }

            const res = await request({
                url: "category/add-category",
                method: "POST",
                data: processedData
            })

            if (res.status === "success") {
                toast.success(res.message)
                // Reset form and chips state
                reset()
                setOptionsInputs({})
                setOptionsChips({})
                setAliasInput('')
                setAliasChips([])
                navigate('/configuration/categories');
            }
        } catch (error: any) {
            console.error('Error adding category:', error);
            toast.error(error || "Error adding category")
        } finally {
            setLoading(false)
        }
    };

    const addNewAttribute = () => {
        append({
            key: '',
            label: '',
            type: 'string',
            required: false,
            options: [],
        });
    };

    const handleOptionInputChange = (index: number, value: string) => {
        setOptionsInputs(prev => ({
            ...prev,
            [index]: value
        }));
    };

    const handleAddOption = (index: number) => {
        const inputValue = optionsInputs[index]?.trim();
        if (!inputValue) return;

        setOptionsChips(prev => {
            const currentChips = prev[index] || [];
            if (currentChips.includes(inputValue)) {
                toast.warning('Option already exists');
                return prev;
            }
            return {
                ...prev,
                [index]: [...currentChips, inputValue]
            };
        });

        setOptionsInputs(prev => ({
            ...prev,
            [index]: ''
        }));
    };

    const handleDeleteOption = (index: number, optionToDelete: string) => {
        setOptionsChips(prev => {
            const currentChips = prev[index] || [];
            return {
                ...prev,
                [index]: currentChips.filter(option => option !== optionToDelete)
            };
        });
    };

    const handleKeyPress = (index: number, event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddOption(index);
        }
    };

    // Function to remove all options for a specific attribute
    const clearAttributeOptions = (index: number) => {
        setOptionsChips(prev => ({
            ...prev,
            [index]: []
        }));
        setOptionsInputs(prev => ({
            ...prev,
            [index]: ''
        }));
    };

    // Alias names handlers
    const handleAliasInputChange = (value: string) => {
        setAliasInput(value);
    };

    const handleAddAlias = () => {
        const inputValue = aliasInput.trim();
        if (!inputValue) return;

        if (aliasChips.includes(inputValue)) {
            toast.warning('Alias name already exists');
            return;
        }

        setAliasChips(prev => [...prev, inputValue]);
        setAliasInput('');
    };

    const handleDeleteAlias = (aliasToDelete: string) => {
        setAliasChips(prev => prev.filter(alias => alias !== aliasToDelete));
    };

    const handleAliasKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddAlias();
        }
    };

    const clearAllAliases = () => {
        setAliasChips([]);
        setAliasInput('');
    };

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>
            <Card elevation={2} sx={{ borderRadius: 1, maxWidth: 1200, mx: 'auto' }}>
                <CardContent sx={{ p: 3 }}>
                    {/* Header Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
                            Add New Category
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create a new product category with custom attributes
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={3}>

                            {/* Basic Information Section - Two Column Layout */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <CategoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Basic Information
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    {/* Category Name and Description Row */}
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        {/* Left Column - Category Name */}
                                        <Box sx={{ flex: 1 }}>
                                            <TextField
                                                size="small"
                                                label="Category Name"
                                                placeholder="e.g., Brake Rotors"
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CategoryIcon color="action" fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                {...register('name', {
                                                    required: 'Category name is required',
                                                    minLength: { value: 2, message: 'Category name must be at least 2 characters' }
                                                })}
                                            />
                                        </Box>

                                        {/* Right Column - Description */}
                                        <Box sx={{ flex: 1 }}>
                                            <TextField
                                                size="small"
                                                label="Description"
                                                placeholder="Brief description of the category"
                                                multiline
                                                rows={2}
                                                fullWidth
                                                error={!!errors.description}
                                                helperText={errors.description?.message}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <DescriptionIcon color="action" fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                {...register('description')}
                                            />
                                        </Box>
                                    </Stack>

                                    {/* Alias Names Section */}
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <AlternateEmailIcon color="primary" fontSize="small" />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    Alias Names
                                                </Typography>
                                            </Stack>
                                            {aliasChips.length > 0 && (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="caption" color="text.secondary">
                                                        {aliasChips.length} alias(es)
                                                    </Typography>
                                                    <Tooltip title="Clear all aliases">
                                                        <IconButton
                                                            size="small"
                                                            onClick={clearAllAliases}
                                                            sx={{ color: 'warning.main' }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            )}
                                        </Stack>

                                        {/* Alias Chips Display */}
                                        <Box sx={{ mb: 1.5, minHeight: 32 }}>
                                            {aliasChips.map((alias, index) => (
                                                <Chip
                                                    key={index}
                                                    label={alias}
                                                    onDelete={() => handleDeleteAlias(alias)}
                                                    color="secondary"
                                                    variant="filled"
                                                    size="small"
                                                    sx={{ m: 0.25 }}
                                                    disabled={loading}
                                                />
                                            ))}
                                        </Box>

                                        {/* Input for adding new aliases */}
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Enter alias name"
                                                    fullWidth
                                                    value={aliasInput}
                                                    onChange={(e) => handleAliasInputChange(e.target.value)}
                                                    onKeyPress={handleAliasKeyPress}
                                                    disabled={loading}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AlternateEmailIcon color="action" fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={handleAddAlias}
                                                disabled={!aliasInput.trim() || loading}
                                                sx={{ minWidth: 60 }}
                                            >
                                                Add
                                            </Button>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Press Enter or click Add to include alias names
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Attributes Template Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <StyleIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Attribute Templates
                                        </Typography>
                                        <Chip
                                            label={`${fields.length} attributes`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addNewAttribute}
                                        size="small"
                                        disabled={loading}
                                    >
                                        Add Attribute
                                    </Button>
                                </Stack>

                                {fields.length === 0 ? (
                                    <Box
                                        sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            border: '2px dashed',
                                            borderColor: 'grey.300',
                                            borderRadius: 1.5,
                                            backgroundColor: 'grey.50'
                                        }}
                                    >
                                        <StyleIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            No attributes added yet
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                            Add custom attributes to define product specifications
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={addNewAttribute}
                                            size="small"
                                            disabled={loading}
                                        >
                                            Add First Attribute
                                        </Button>
                                    </Box>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {fields.map((field, index) => (
                                            <Paper
                                                key={field.id}
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 1.5,
                                                    borderColor: 'grey.200',
                                                    backgroundColor: 'white',
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    {/* Attribute Header */}
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="body1" fontWeight={600}>
                                                            Attribute {index + 1}
                                                        </Typography>

                                                        <Stack direction="row" spacing={0.5}>
                                                            {watch(`attributesTemplate.${index}.type`) === 'select' && (
                                                                <Tooltip title="Clear all options">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => clearAttributeOptions(index)}
                                                                        sx={{ color: 'warning.main' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title="Remove Attribute">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => remove(index)}
                                                                    sx={{ color: 'error.main' }}
                                                                    disabled={loading}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Stack>

                                                    {/* Key and Label Fields - Two Column */}
                                                    <Stack direction="row" spacing={1.5}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <TextField
                                                                size="small"
                                                                label="Key"
                                                                placeholder="e.g., rotorType"
                                                                fullWidth
                                                                error={!!errors.attributesTemplate?.[index]?.key}
                                                                helperText={errors.attributesTemplate?.[index]?.key?.message}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <KeyIcon color="action" fontSize="small" />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                {...register(`attributesTemplate.${index}.key`, {
                                                                    required: 'Key is required',
                                                                    pattern: {
                                                                        value: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                                                                        message: 'Key must start with a letter or underscore'
                                                                    }
                                                                })}
                                                            />
                                                        </Box>

                                                        <Box sx={{ flex: 1 }}>
                                                            <TextField
                                                                size="small"
                                                                label="Display Label"
                                                                placeholder="e.g., Rotor Type"
                                                                fullWidth
                                                                error={!!errors.attributesTemplate?.[index]?.label}
                                                                helperText={errors.attributesTemplate?.[index]?.label?.message}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <LabelIcon color="action" fontSize="small" />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                {...register(`attributesTemplate.${index}.label`, {
                                                                    required: 'Display label is required'
                                                                })}
                                                            />
                                                        </Box>
                                                    </Stack>

                                                    {/* Type and Required Fields - Two Column */}
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                        <Box sx={{ flex: 1 }}>
                                                            <Controller
                                                                name={`attributesTemplate.${index}.type`}
                                                                control={control}
                                                                rules={{ required: 'Data type is required' }}
                                                                render={({ field, fieldState: { error } }) => (
                                                                    <TextField
                                                                        {...field}
                                                                        select
                                                                        size="small"
                                                                        label="Data Type *"
                                                                        fullWidth
                                                                        error={!!error}
                                                                        helperText={error?.message}
                                                                        InputProps={{
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    <StyleIcon color="action" fontSize="small" />
                                                                                </InputAdornment>
                                                                            ),
                                                                        }}
                                                                        disabled={loading}
                                                                    >
                                                                        {attributeTypes.map((type) => (
                                                                            <MenuItem key={type.value} value={type.value}>
                                                                                {type.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                )}
                                                            />
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', height: 40, mt: 0.5 }}>
                                                            <Controller
                                                                name={`attributesTemplate.${index}.required`}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                {...field}
                                                                                checked={field.value}
                                                                                color="primary"
                                                                                size="small"
                                                                                disabled={loading}
                                                                            />
                                                                        }
                                                                        label="Required"
                                                                    />
                                                                )}
                                                            />
                                                        </Box>
                                                    </Stack>

                                                    {/* Options for Select Type with Chips */}
                                                    {watch(`attributesTemplate.${index}.type`) === 'select' && (
                                                        <Box>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                    OPTIONS
                                                                </Typography>
                                                                {(optionsChips[index] || []).length > 0 && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {optionsChips[index].length} option(s)
                                                                    </Typography>
                                                                )}
                                                            </Stack>

                                                            {/* Chips Display */}
                                                            <Box sx={{ mb: 1.5, minHeight: 32 }}>
                                                                {(optionsChips[index] || []).map((option, optionIndex) => (
                                                                    <Chip
                                                                        key={optionIndex}
                                                                        label={option}
                                                                        onDelete={() => handleDeleteOption(index, option)}
                                                                        color="primary"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        sx={{ m: 0.25 }}
                                                                        disabled={loading}
                                                                    />
                                                                ))}
                                                            </Box>

                                                            {/* Input for adding new options */}
                                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                                <Box sx={{ flex: 1 }}>
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder="Enter option"
                                                                        fullWidth
                                                                        value={optionsInputs[index] || ''}
                                                                        onChange={(e) => handleOptionInputChange(index, e.target.value)}
                                                                        onKeyPress={(e) => handleKeyPress(index, e)}
                                                                        disabled={loading}
                                                                    />
                                                                </Box>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleAddOption(index)}
                                                                    disabled={!optionsInputs[index]?.trim() || loading}
                                                                    sx={{ minWidth: 60 }}
                                                                >
                                                                    Add
                                                                </Button>
                                                            </Stack>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                                Press Enter or click Add to include options
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </Paper>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/configuration/categories')}
                                    size="medium"
                                    color='secondary'
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    sx={{ minWidth: 100 }}
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>
        </>
    );
};

export default AddCategory;